/**
 * 오너 플랜 기능 체크 Hook
 * - 현재 플랜 정보 조회
 * - 기능 사용 가능 여부 확인
 * - 제한 사항 확인
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../../../infrastructure/api';

interface MenuPermissions {
  plan: string;
  plan_display_name: string;
  available_features: string[];
  restricted_features: string[];
}

export const usePlanFeatures = () => {
  const [permissions, setPermissions] = useState<MenuPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMenuPermissions();
  }, []);

  const fetchMenuPermissions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v1/owner/subscription/menu-permissions');
      setPermissions(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류');
      // 기본 권한 설정 (에러 시) - 베이직 플랜 기본 기능
      setPermissions({
        plan: 'basic',
        plan_display_name: '베이직',
        available_features: ['좌석 관리', '순공부시간', '얼굴 인식', '회원 관리', '입퇴실 기록', '결제 관리'],
        restricted_features: []
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * 특정 기능 사용 가능 여부 확인
   */
  const hasFeature = (featureName: string): boolean => {
    if (!permissions) return false;
    return permissions.available_features.includes(featureName);
  };

  /**
   * 특정 기능이 제한되어 있는지 확인
   */
  const isRestricted = (featureName: string): boolean => {
    if (!permissions) return true;
    return permissions.restricted_features.includes(featureName);
  };

  /**
   * 프리미엄 플랜 여부
   */
  const isPremium = (): boolean => {
    return permissions?.plan === 'premium';
  };

  /**
   * 스탠다드 플랜 여부
   */
  const isStandard = (): boolean => {
    return permissions?.plan === 'standard';
  };

  /**
   * 플랜 표시 이름
   */
  const getPlanDisplayName = (): string => {
    return permissions?.plan_display_name || '무료';
  };

  return {
    permissions,
    loading,
    error,
    hasFeature,
    isRestricted,
    isPremium,
    isStandard,
    getPlanDisplayName,
    refresh: fetchMenuPermissions
  };
};
